import * as React from 'react';
import { Format, formatAsElementId } from './Utils';
import { DataGrid, DataGridCtor } from './DataGrid';
import { DataGridColumn } from './DataGrid.Column';
import { FormComponent } from './Form';
import { Res } from '../../api';
import { showCustom } from './Dialog';
import classnames from 'classnames';
import Button from './Button';
import AutoCompleteTextBox from './AutoCompleteTextBox';
import { Binding, Result } from 'core/infrastructure';
import { deepEquals } from 'core/utils';
import { RemoteMultiSelectDialog } from './RemoteMultiSelectDialog';


interface RemoteMultiSelectListProps<T> extends React.HTMLProps<RemoteMultiSelectList<T>> {
	linkTo: Binding.PropertyPath<T>;
	gridHeight?: number;
	allowReorder?: boolean;
	source: (filter: string) => Promise<any[]>;
	itemKey: (item: any) => any;
	columns: DataGridColumn<any>[];
	itemFormat?: Format;
	dialogTitle: string;
	dialogHelpText: string;
	boundValue?: any[];
	formId?: string;
	useSingleSelection?: boolean;
	onSelectedItemsChanged?: (selectedItems: any[]) => void;
}

interface MultiSelectListState {
	errorMsg?: string;
	selectedItems: any[];
}


function removeByItemKey<T>(allItems: T[], itemsToRemove: T[], itemKey: (item: T) => any): T[] {
	const containsByKey = (items: T[], item: T) => items.some(x => deepEquals(itemKey(x), itemKey(item)));
	return allItems.filter(item => !containsByKey(itemsToRemove, item));
}

export class RemoteMultiSelectList<T> extends React.Component<RemoteMultiSelectListProps<T>, MultiSelectListState> implements FormComponent {

	private dataGrid = React.createRef<DataGrid<any>>();
	private input = React.createRef<AutoCompleteTextBox<T>>();

	constructor(props: RemoteMultiSelectListProps<T>) {
		super(props);

		if (!(props.boundValue instanceof Array))
			throw new Error('MultiSelectList no tiene un boundValue de tipo Array');

		this.state = this.createInitialState(props);
	}

	createInitialState(props: RemoteMultiSelectListProps<T>): MultiSelectListState {
		const selectedItems = props.boundValue || [];
		return {
			errorMsg: '',
			selectedItems: [...selectedItems],
		};
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	getValue() {
		this.setState({ errorMsg: '' });
		return Result.ok(this.getSelectedItems());
	}

	getSelectedItems() {
		// Este método hace falta par los tests
		return this.state.selectedItems;
	}

	getPendingSource() {
		const search = this.props.source;
		return (filter: string) => search(filter).then(options => removeByItemKey(options, this.getSelectedItems(), this.props.itemKey));
	}

	protected buildSelectItemsDialog() {

		const gridProps = {
			source: this.getPendingSource(),
			columns: this.props.columns,
			itemKey: this.props.itemKey
		};

		return <RemoteMultiSelectDialog
			title={this.props.dialogTitle}
			helpText={this.props.dialogHelpText}
			grid={gridProps}
			acceptOnEnterKey={false} />;
	}

	protected showSelectItemsDialog() {
		return showCustom<any[]>(this.buildSelectItemsDialog());
	}

	buildAutoComplete() {

		const onOptionSelected = (option: any) => {
			const selectedItems = [...this.getSelectedItems()];
			selectedItems.push(option);
			this.setState({ selectedItems }, () => {
				this.dataGrid.current!.selectItems([option]);
			});
		};

		const availableSource = this.getPendingSource();

		const onShowDialog = () => this.showSelectItemsDialog().then(result => {
			const selectedItems = [...this.getSelectedItems()];
			if (result.ok)
				this.setState({ selectedItems: selectedItems.concat(result.value) }, () => {
					this.dataGrid.current!.selectItems(this.props.useSingleSelection ? [result.value[0]] : result.value);
				});
			this.input.current!.focus();
		});

		return <AutoCompleteTextBox
			ref={this.input}
			placeholder={Res.WA.WA_MULTI_SELECT_LIST_ADD_PLACE_HOLDER()}
			availableOptions={availableSource}
			formatOption={this.props.itemFormat}
			onShowSearchDialog={onShowDialog}
			onOptionSelected={onOptionSelected}
			errorMsg={this.state.errorMsg || ''} />;
	}

	// Esta función dejo que sea accesible desde el exterior por si queremos eliminar
	// desde un botón de una columna
	removeItems(items: any[]) {
		const newSelection = removeByItemKey(this.getSelectedItems(), items, this.props.itemKey);
		this.setState({ selectedItems: newSelection });
	}

	render() {

		const Grid: DataGridCtor<any> = DataGrid;

		const onRemoveItems = () => this.removeItems(this.dataGrid.current!.getSelectedItems());

		const onMoveItemUp = () => {
			const selectedItems = [...this.getSelectedItems()];
			const index = selectedItems.indexOf(this.dataGrid.current!.getSelectedItems()[0]);
			if (index > 0) {
				selectedItems.moveItem(index, index - 1);
				this.setState({ selectedItems }, () =>
					this.dataGrid.current!.selectItems([selectedItems[index - 1]])
				);
			}
		};

		const onMoveItemDown = () => {
			const selectedItems = [...this.getSelectedItems()];
			const index = selectedItems.indexOf(this.dataGrid.current!.getSelectedItems()[0]);
			if (index < selectedItems.length - 1) {
				selectedItems.moveItem(index, index + 1);
				this.setState({ selectedItems }, () =>
					this.dataGrid.current!.selectItems([selectedItems[index + 1]])
				);
			}
		};

		const columns = this.props.columns;

		const orderButtons = <div className='m-t'>
			<Button id={'test_up_button'} onClick={onMoveItemUp}><i className='text-info fa fa-arrow-up'></i></Button>
			<Button id={'test_down_button'} className='m-t-xs' onClick={onMoveItemDown}><i className='text-info fa fa-arrow-down'></i></Button>
		</div>;

		const onSelectedItemsChanged = (items: any[]) => {
			if (this.props.onSelectedItemsChanged)
				return this.props.onSelectedItemsChanged(items);
		};

		return <div className={classnames({ 'form-group': true, 'has-error': !!this.state.errorMsg })}>

			<div className='col-sm-12'>
				{this.buildAutoComplete()}

				<div className='m-t'>
					<div className='multi-select-list'>
						<Grid
							height={this.props.gridHeight || 200}
							itemKey={this.props.itemKey}
							ref={this.dataGrid}
							id={'dataGrid_' + formatAsElementId(this.getPath(), this.props.formId || '')}
							columns={columns}
							initialData={this.getSelectedItems()}
							shouldPersistState={false}
							sortable={!this.props.allowReorder}
							multiSelect={!this.props.useSingleSelection}
							hideExportButton={true}
							maxHeight={this.props.gridHeight || 175}
							onSelectedItemsChanged={onSelectedItemsChanged} />
					</div>
					<div className='multi-select-list-buttons'>
						<Button onClick={onRemoveItems}><i className='text-info fa fa-trash-o'></i></Button>
						{this.props.allowReorder ? orderButtons : null}
					</div>

				</div>
			</div>
		</div>;
	}
}

export type RemoteMultiSelectListCtor<T> = new (props: RemoteMultiSelectListProps<T>) => React.Component<RemoteMultiSelectListProps<T>, MultiSelectListState>;
